import { FormInstance } from "src/assets/imports/antdComponents";
import { UserAccessTypeString } from "src/pages/Studies/models/interfaces";

export type ProvisionScope = "both" | "zs_only" | "client_only";

export enum ProfileActionType {
	Add = "add",
	Edit = "edit",
	Delete = "delete",
}

export enum UserObservabilityVersionType {
	v1 = "v1.0",
	v2 = "v2.0",
}

interface ModifiedBy {
	full_name: string;
	user_id: string;
	email: string;
}

export interface User {
	id: string;
	collaborator_name: string;
	collaborator_id: string;
	collaborator_email: string;
	version_history: { modified_dt: string; modified_by: ModifiedBy };
	profiles: string[];
	profile_access?: any;
	enable_access: boolean;
	study_data: [];
	roles: any;
	// Time-limited access. Render rules in the user table:
	//   - is_superadmin true → "Never" (allowlisted, can't expire).
	//   - null on a non-superadmin → "Not set" (no expiry configured yet).
	//   - non-null but user's roles aren't in the time-limited-access feature's
	//     enforcement list → render the date muted/strikethrough (informational only).
	//   - non-null + enforced → colour-coded urgency rendering.
	access_expires_at?: string | null;
	is_superadmin?: boolean;
}

export interface IUserDetails {
	email: string;
	profiles?: string[];
	profile_access?: any;
	delete?: boolean;
	enable_access?: boolean;
	roles?: any;
	id?: string;
	name?: string;
	emp_id?: string;
	// UI/display field. The DatePicker writes a "YYYY-MM-DD" string here (or an ISO timestamp on
	// edit-mode prefill from the GET response). This value is *never* sent on the wire — the
	// save handler emits the YYYY-MM-DD string under the BE's expected key
	// `access_duration_days` and strips this field before the API call.
	access_expires_at?: string | null;
	// Wire-only field. Despite the name, the BE accepts this as a calendar date string in
	// "YYYY-MM-DD" format (interpreted as end-of-day UTC), not an integer day count. The form
	// does NOT bind to it directly; `handleSaveUser` derives the YYYY-MM-DD string from
	// `access_expires_at` so the DatePicker UX stays intact. Required by the BE for POST;
	// on edit, omit when the admin didn't touch the picker so the existing expiry isn't reset.
	access_duration_days?: string;
	is_superadmin?: boolean;
}

export interface IAddRole {
	profile_name: string;
	application_instance: string;
}

export interface IProfile {
	profile: string;
	profile_id?: string;
	delete?: boolean;
	primary_language?: string;
	secondary_language?: string[];
	display_profile_name?: string;
	provision_scope?: ProvisionScope;
}

export interface IRole {
	role_name: string;
	role_id?: string;
	delete?: boolean;
	isEditable?: boolean;
	provision_scope?: ProvisionScope;
}

export interface IUserTable {
	tableData: User[];
	copiedData: User[];
}

export interface AddProfileModalProps {
	form: FormInstance;
	handleClose: () => void;
	handleSave: () => void;
	isModalVisible: boolean;
	setIsModalVisible: (val: boolean) => void;
	profileData: {
		profile?: string;
		primary_language?: string;
		secondary_language?: string[];
		display_profile_name?: string;
		provision_scope?: ProvisionScope;
	};
	setProfileData: (data: any) => void;
	isProfileSaving: boolean;
	type: string;
}

export interface IAddRoleModalProps {
	form: any;
	handleClose: (type?: string) => void;
	handleSave: () => void;
	isModalVisible: boolean;
	setIsModalVisible: (val: boolean) => void;
	roleData: IRole;
	setRoleData: React.Dispatch<React.SetStateAction<IRole>>;
	isRoleSaving: boolean;
	type: string;
}

interface TableData {
	collaborator_email: string;
	collaborator_name: string;
}

interface ErrorData {
	[key: string]: any; // Define the structure as per your data
}

export interface AddUserModalProps {
	form: FormInstance;
	handleClose: () => void;
	handleSave: () => void;
	isModalVisible: boolean;
	getListOfUsers: () => void;
	emailError: string | null;
	setEmailError: React.Dispatch<React.SetStateAction<string | null>>;
	isSaving: boolean;
	profileError?: string | null;
	setProfileError?: React.Dispatch<React.SetStateAction<string | null>>;
	// Edit-mode contract: parent must omit access_expires_at from PUT /admin/user when the admin
	// did not touch the field, so backend keeps the existing expiry untouched (no clock reset).
	onAccessDurationTouchedChange?: (touched: boolean) => void;
	// Lowercased role names the BE will actually enforce access_expires_at for.
	// Used to surface a non-blocking warning when none of the current user's
	// roles fall inside this set — the date will be stored but won't disable
	// the user. Mirrors the muted/strikethrough styling in UserTable.
	enforcedAccessExpiryRoles?: Set<string>;
	areEnforcedRolesLoaded?: boolean;
	// Display name from feature_name so warning copy reads "Time-Limited Access"
	// instead of leaking the feature_key.
	timeLimitedFeatureName?: string;
}

export interface IStudyType {
	study_type_id: string;
	study_type_name: string;
}

export interface IGeography {
	_id: string;
	drop_down_title: string;
	drop_down_type: string;
}

export interface ICustomEntityTag {
	_id: string;
	drop_down_title: string;
	drop_down_type: string;
	profile: string;
}

export interface IUser {
	full_name: string;
	user_id: string;
	email: string;
}

export interface IVersionHistory {
	created_dt: string;
	created_by: IUser;
	modified_dt: string;
	modified_by: IUser;
}

export interface ICollaborator {
	collaborator_id: string;
	collaborator_name: string;
	collaborator_email: string;
	user_access_type: UserAccessTypeString;
	is_owner?: boolean;
	is_active?: boolean;
}

export interface IProfileMapping {
	profile_name: string;
	isActive: boolean;
	primary_language: {
		name: string;
		code: string;
		is_primary: boolean;
	};
}

export interface IStudy {
	study_id: string;
	study_name: string;
	study_type: IStudyType;
	metadata: {
		description: string;
		start_dt: string | null;
		end_dt: string | null;
		geography: IGeography;
		custom_entity_tags: ICustomEntityTag[];
	};
	version_history: IVersionHistory;
	study_collaborators: ICollaborator[];
	project_code: string;
	count_of_files: number;
	profile_mapping: IProfileMapping;
	is_soft_delete: boolean;
	ask_kural_queries: null;
}

export interface IStudyManagement {
	activeTab: string;
}

export interface IStudyPayload {
	currentPage: number;
	pageSize: number;
	totalPages: number | null;
	studiesData: IStudy[];
}

export interface IStudyFilters {
	search: string | null;
	selectedFilters: string[];
	selectedStudyTypes: string[];
	isProfileFiltered: boolean;
	isStudyTypeFiltered: boolean;
	sort_ascending: boolean | null;
}

export interface IStudyTableProps {
	handlePageChange: (pageNo: number) => void;
	handleStudySearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
	getListOfStudy: (pageNo: number, loading: boolean, tableLoading: boolean, profiles?: string[], sort_ascending?: boolean, pageSize?: number) => {};
	tableLoading: boolean;
	handleProfileFilter: (arr: string[]) => void;
	studiesPayload: IStudyPayload;
	studiesfilter: IStudyFilters;
	setStudiesFilter: (obj: IStudyFilters) => void;
	setStudiesPayload: (obj: IStudyPayload) => void;
}

export interface IModifiedHistory {
	modified_dt: string;
	modified_by: {
		full_name: string;
		user_id: string;
		email: string;
	};
}

export type MetadataPrimitive = number | string | boolean | null;

export interface AllowedMetadataConstraints {
	gt?: number;
	ge?: number;
	lt?: number;
	le?: number;
	min_length?: number;
	max_length?: number;
	pattern?: string;
}

export interface AllowedMetadataField {
	// BE currently emits Python type names ("float", "int", "str", "bool").
	// Kept as string to tolerate future stable contract values like "number" | "integer" | …
	type: string;
	required: boolean;
	// Human-friendly name shown in the UI. The field key (e.g. "user_1d") is only an
	// internal payload identifier and must not be surfaced to admins.
	label?: string | null;
	description: string | null;
	constraints?: AllowedMetadataConstraints;
	// Optional grouping key used to enforce mutual-exclusion rules (see mutually_exclusive_groups).
	group?: string | null;
	// When true, the field is not selectable in the UI dropdown (BE-controlled visibility).
	hidden?: boolean;
}

// A set of group names that cannot be configured at the same time within one version.
// e.g. [["profile", "tenant"]] means profile-* and tenant-* params are mutually exclusive.
export type MutuallyExclusiveGroups = string[][];

export interface AllowedMetadata {
	fields: Record<string, AllowedMetadataField>;
	extra_keys_allowed: boolean;
	mutually_exclusive_groups?: MutuallyExclusiveGroups;
}

export interface IVersion {
	key?: string;
	version_label: string;
	description: string;
	enabled: boolean;
	roles: any;
	activation_date: string | null;
	expiration_date: string | null;
	isFallback?: boolean;
	is_beta?: boolean;
	metadata?: Record<string, MetadataPrimitive>;
	allowed_metadata?: AllowedMetadata;
}

export interface IFeature {
	profile_name?: string;
	feature_id?: string;
	isDefault?: boolean;
	feature_key: string;
	feature_name: string;
	isAdminFeature?: boolean;
	isEnabled?: boolean;
	version_list: IVersion[];
	modified_history?: IModifiedHistory;
	dependencies?: any;
}

export interface IMetadataValidationRecord {
	version_label: string;
	key: string;
	message: string;
}

export interface IMetadataValidationError {
	type: "metadata";
	message: string;
	record?: IMetadataValidationRecord[] | null;
}

export interface IFeatureManagement {
	profile_name: string;
	features: IFeature[];
}

export interface IProfileLanguageMapping {
	profile_name: string;
	primary_language: string;
	secondary_language: string[] | null;
	isActive: boolean;
	isDefault: boolean;
}

export interface IWindowCount {
	"1d": number;
	"1w": number;
}

export interface IActiveUserCount {
	tenant: IWindowCount;
	profiles: Record<string, IWindowCount>;
}

export interface IFeatureModalProps {
	form: FormInstance;
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (values: IFeature) => void;
	initialValues?: IFeature | null;
	isEdit?: boolean;
	isDefaultProfile: boolean;
	existingFeatures: any;
	versionValidationError?: {
		type: string;
		record: string[] | null;
		message: string;
	} | null;
	onClearVersionError: () => void;
	metadataValidationError?: IMetadataValidationError | null;
	onClearMetadataError?: () => void;
	activeUserCount?: IActiveUserCount;
}

export interface IDefaultFeatureTableProps {
	loading: boolean;
	switchLoading: boolean;
	featuresData: IFeature[];
	onEdit: (record: any) => void;
	onDelete: (record: any) => void;
	onStatusChange: (featureKey: string, feature_name: string, checked: boolean) => void;
}

export interface ICustomProfileTableProps {
	loading: boolean;
	profilesData: IFeatureManagement[];
	onEdit: (record: any) => void;
	onReset: (record: any) => void;
	onStatusChange: (profileName: string, featureKey: string, checked: boolean) => void;
}

export interface IFeatureTableRecord {
	key: string;
	feature_key: string;
	feature_name: string;
	isEnabled: boolean;
	version: IVersion | null;
	modified_history: {
		modified_dt?: string;
		modified_by?: {
			full_name?: string;
		};
	};
	isFirstRow: boolean;
	hasVersions: boolean;
	rowSpan?: number;
}

export interface IFilterState {
	feature_name: string | null;
	roles: string | null;
}

export interface IProfileData {
	profile_name: string;
	profile_id?: string;
	primary_language: string;
	secondary_language: string[];
	whitelisted_users: IWhitelistedUser[];
	isActive: boolean;
	display_profile_name?: string;
	provision_scope?: ProvisionScope;
}

export interface IWhitelistedUser {
	collaborator_id: string;
	collaborator_name: string;
	collaborator_email: string;
	user_access_type: string;
}

export interface IEventDescription {
	user?: {
		email: string;
		name: string;
		emp_id: string;
	};
	feature?: {
		feature_id?: string;
		feature_key: string;
		feature_name: string;
	};
	study?: {
		study_id: string;
		study_name: string;
		profile?: string;
	};
	profile?: {
		profile_id?: string;
		profile_name: string;
		primary_language: string;
		secondary_languages: string[];
	};
	role?: {
		role_id?: string;
		role_name: string;
	};
	operation: string;
}

export interface IEventLog {
	_id: string;
	request_id: string;
	timestamp: string;
	current_user: {
		emp_id: string;
		email: string;
		name: string;
	};
	category: string;
	action: string;
	sub_action: string | null;
	description: IEventDescription[];
	status: {
		success: boolean;
		error_type: string | null;
		message: string;
	};
	metadata: {
		endpoint: string;
		execution_time: number;
	};
}

export interface IEventLogsState {
	currentPage: number;
	pageSize: number;
	totalPages: number;
	eventLogsData: IEventLog[];
}

export interface IEventLogsResponse {
	logs: IEventLog[];
	total: number;
	page: number;
	limit: number;
	total_pages: number;
}

export interface IUserTableProps {
	onSelectChange: (selectedRowKeys: any[]) => void;
	handleRowToggle: (record: User) => void;
	handleDelete: (record: User) => void;
	handleEdit: (record: User) => void;
	isDeleting: boolean;
	togglingIds: Set<string>;
	handleSearch: (e: React.ChangeEvent<HTMLInputElement> | null, val: string) => void;
	handleSearchReset: () => void;
	localSearch: string;
	pagination?: {
		current: number;
		pageSize: number;
		total: number;
		showSizeChanger: boolean;
	};
	onChange?: (pagination: any, filters: any, sorter: any) => void;
	handleProfileFilter?: (filters: string[]) => void;
	handleRoleFilter?: (filters: string[]) => void;
	handleAccessFilter?: (filters: string[]) => void;
	userFilters?: {
		selectedProfiles: string[];
		selectedRoles: string[];
		isProfileFiltered: boolean;
		isRoleFiltered: boolean;
		enableAccessFilter?: boolean | null;
		isAccessFiltered?: boolean;
	};
	sortState?: {
		sortAscending?: boolean;
		expiryAscending?: boolean;
	};
	// Lowercased role names the BE will actually enforce access_expires_at for,
	// resolved from the time-limited-access feature's role list. Empty until the
	// features fetch resolves; the table renders the enforced styling in the
	// meantime so we don't flash a misleading "informational only" state.
	enforcedAccessExpiryRoles?: Set<string>;
	areEnforcedRolesLoaded?: boolean;
	// Display name from feature_name so UX copy reads "Time-Limited Access"
	// instead of leaking the feature_key. Defaults to the canonical label in
	// the resolver when the feature is missing from the BE response.
	timeLimitedFeatureName?: string;
}

export interface IUserManagementProps {
	registerGetUsersFunction?: (
		getUsers: (page?: number, pageSize?: number, sortAscending?: boolean, searchValue?: string, profileFilters?: string[], roleFilters?: string[]) => Promise<void>,
		userPayload: any,
		userFilters: any,
	) => void;
}

export interface IServiceAccountManagementProps {
	registerGetServiceAccountsFunction?: (getServiceAccounts: Function, serviceAccountPayload: any, serviceAccountFilters: any) => void;
}

export interface ISubHeaderProps {
	activeTab: string;
	getListOfUsers?: (page?: number, pageSize?: number, sortAscending?: boolean, searchValue?: string, profileFilters?: string[], roleFilters?: string[]) => Promise<void>;
	userPayload?: {
		currentPage: number;
		pageSize: number;
		sortAscending: boolean;
		expiryAscending?: boolean;
	};
	userFilters?: {
		selectedProfiles: string[];
		selectedRoles: string[];
		enableAccessFilter?: boolean | null;
	};
	searchText?: string;
	resetFilters?: (() => void) | null;
}

export const enum MessageTypes {
	INSUFFICIENT_PERMISSION = "Insufficient permission",
}

/* ---------------------------------------------
 User Observability Types
----------------------------------------------*/

/**
 * Time period values supported by export API
 */
export enum TimePeriod {
	THIS_WEEK = "this_week",
	LAST_4_WEEKS = "last_4_weeks",
	LAST_3_MONTHS = "last_3_months",
	THIS_MONTH = "this_month",
	SIX_MONTHS = "6_months",
	THIS_YEAR = "this_year",
	TILL_DATE = "till_date",
}

/**
 * Select dropdown option for users
 */
export interface IUserSelectOption {
	label: string;
	value: string; // email
}

/**
 * Export request params
 */
export interface IExportUserObservabilityParams {
	user_emails: string[];
	time_period: TimePeriod;
	granularity?: "day" | "week" | "month";
}

/**
 * Export API response
 */
export interface IExportUserObservabilityResponse {
	data: {
		url: string;
		file_name: string;
		expires_in: number;
	};
}

export interface IServiceAccountProfileAccess {
	profile_name: string;
	profile_id: string;
	study_access_type: string; // "Full Access" | future values
}

// models/interfaces.ts

export interface IAccountCollaborator {
	collaborator_id: string;
	collaborator_name: string;
	collaborator_email: string;
}

export interface IServiceAccount {
	id: string;
	name: string;
	email?: string;
	description?: string;
	roles: string[];
	profile_access: IServiceAccountProfileAccess[];
	enable_access: boolean;
	is_service_account: boolean;
	created_at?: string;
	modified_dt?: string;
	api_key_count?: number;
	entity_count?: number;
	collaborators?: IAccountCollaborator[]; // ✅ Updated type
}

export interface IServiceAccountKey {
	service_account_id: string;
	label: string;
	expires_in_days?: number;
}

// ============ NOTIFICATION ACTIONS ============

export interface ToggleNotificationActionPayload {
	action_key: string;
	enabled: boolean;
}

export interface UpdateNotificationActionPayload {
	action_key: string;
	delay_minutes: number;
	email_config: {
		to: string[];
		cc: string[];
		bcc: string[];
		greeting: string;
		subject: string;
		content_sections: Record<string, string>;
		theme_color: string;
	};
}

export interface IAvailableVariable {
	name: string;
	description: string;
	example: string;
	type: "universal" | "local";
	optional: boolean;
}

export interface IEmailConfig {
	to: string[];
	cc: string[];
	bcc: string[];
	subject: string;
	greeting: string;
	theme_color: string;
	content_sections: Record<string, string>;
}

export interface IEmailAlert {
	action_key: string;
	display_name: string;
	description: string;
	category: string;
	enabled: boolean;
	delay_minutes: number;
	email_config: IEmailConfig;
	available_variables: IAvailableVariable[];
	version_history: {
		modified_dt: string;
		modified_by: {
			full_name: string;
			user_id: string;
			email: string;
		};
	};
}

export interface AuditLogExportPayload {
	start_date: string;
	end_date: string;
	user_filter: "both" | "zs" | "client";
	mask_ids: boolean;
}

export type AskKuralUserFilter = "zs" | "client" | "service_account";

export type AskKuralExtraColumn = "study_type" | "mode" | "rating" | "entity_id" | "file_id" | "meta_id" | "feedback_description" | "is_active" | "message_content";

export interface AskKuralLogsExportPayload {
	start_date: string;
	end_date: string;
	user_filters: AskKuralUserFilter[];
	extra_columns?: AskKuralExtraColumn[];
}

export interface IPermanentDeletePayload {
	study_id: string;
	email_subject: string;
	requestor_email: string;
	executor_notes: string;
}

export interface IPermanentDeleteResponse {
	message: string;
	audit_id: string;
	partial?: boolean;
}

export interface IPermanentDeleteModalProps {
	isVisible: boolean;
	studyId: string;
	studyName: string;
	profileName: string;
	onCancel: () => void;
	onConfirm: (payload: IPermanentDeletePayload) => void;
	isLoading?: boolean;
}

export interface IAdminTranscriptFilters {
	therapy_area?: string;
	indication?: string;
	research_type?: string;
	uploaded_by?: string;
	search?: string;
	upload_start?: string;
	upload_end?: string;
	study_start?: string;
	study_end?: string;
}

export interface IAdminTranscriptRow {
	entity_id: string;
	entity_name: string;
	entity_profile?: string;
	file_id: string;
	active_transcript_id?: string;
	file_name: string;
	therapy_area?: string;
	indication?: string;
	research_type?: string;
	uploaded_by?: string;
	upload_dt?: string;
	study_start_dt?: string;
	study_end_dt?: string;
	modified_dt?: string;
	status?: string;
	tags: string[];
	metadata: Record<string, unknown>;
}

export interface IAdminTranscriptStats {
	total_transcripts: number;
	total_studies: number;
	therapy_area_count: number;
	indication_count: number;
	research_type_count: number;
	uploaded_by_count: number;
}

export interface IAdminTranscriptStagingRefreshResponse {
	scanned_entities: number;
	staged_transcripts: number;
	deactivated_transcripts: number;
	metadata_contract_version: string;
}

export interface IAdminTranscriptFacets {
	therapy_areas: string[];
	indications: string[];
	research_types: string[];
	uploaders: string[];
	metadata_keys: string[];
}

export interface IAdminTranscriptListResponse {
	rows: IAdminTranscriptRow[];
	total: number;
	page: number;
	page_size: number;
	stats: IAdminTranscriptStats;
	facets: IAdminTranscriptFacets;
}

export interface IAdminTranscriptExportItem {
	entity_id: string;
	file_id: string;
	active_transcript_id?: string;
}

export interface IAdminTranscriptExportPayload {
	select_all: boolean;
	filters?: IAdminTranscriptFilters;
	transcripts?: IAdminTranscriptExportItem[];
}
